import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { QuietEmpty, WorkerChip } from './shared';
import { useSessionHistory, useStationDaily } from '@/features/workMode/hooks';
import { formatDuration } from '@/features/workMode/api';
import { END_REASON_LABEL } from '@/features/workMode/labels';
import { scrapRate } from '@/features/workMode/gamification';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { WorkSession } from '@/features/workMode/types';
import { formatDateOnly, formatDateTime } from '@/lib/formatters';

interface HistoryTabProps {
  lookups: WorkLookups;
}

const RANGES = [7, 14, 30, 90] as const;

/** What the floor produced, day by day and shift by shift. */
export function HistoryTab({ lookups }: HistoryTabProps) {
  const [days, setDays] = useState<(typeof RANGES)[number]>(14);
  const { data: daily = [], isLoading: dailyLoading } = useStationDaily(days);
  const { data: sessions = [], isLoading: sessionsLoading } = useSessionHistory(days);
  const [shown, setShown] = useState(40);

  const perDay = useMemo(() => {
    const map = new Map<string, { good: number; scrap: number; workers: number; cycles: number[] }>();
    for (const r of daily) {
      const key = r.day.slice(0, 10);
      const cur = map.get(key) ?? { good: 0, scrap: 0, workers: 0, cycles: [] };
      cur.good += r.units;
      cur.scrap += r.scrapped;
      cur.workers += r.workers;
      if (r.avg_cycle_seconds != null && r.units > 0) cur.cycles.push(r.avg_cycle_seconds);
      map.set(key, cur);
    }
    const out: Array<{ key: string; good: number; scrap: number; workers: number; avgCycle: number | null }> = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const row = map.get(key);
      out.push({
        key,
        good: row?.good ?? 0,
        scrap: row?.scrap ?? 0,
        workers: row?.workers ?? 0,
        avgCycle: row && row.cycles.length ? row.cycles.reduce((a, b) => a + b, 0) / row.cycles.length : null,
      });
    }
    return out;
  }, [daily, days]);

  const totals = useMemo(() => {
    const good = perDay.reduce((a, d) => a + d.good, 0);
    const scrap = perDay.reduce((a, d) => a + d.scrap, 0);
    const active = perDay.filter((d) => d.good + d.scrap > 0).length;
    return { good, scrap, rate: scrapRate(good, scrap), perActiveDay: active ? good / active : 0 };
  }, [perDay]);

  const max = Math.max(1, ...perDay.map((d) => d.good + d.scrap));

  const byStation = useMemo(() => {
    const map = new Map<string, { units: number; scrapped: number; cycles: number[]; days: Set<string> }>();
    for (const r of daily) {
      const cur = map.get(r.workstation_id) ?? { units: 0, scrapped: 0, cycles: [], days: new Set<string>() };
      cur.units += r.units;
      cur.scrapped += r.scrapped;
      if (r.avg_cycle_seconds != null && r.units > 0) cur.cycles.push(r.avg_cycle_seconds);
      cur.days.add(r.day);
      map.set(r.workstation_id, cur);
    }
    return [...map.entries()]
      .map(([id, v]) => ({
        id,
        name: lookups.stationName(id) ?? 'Unknown',
        units: v.units,
        scrapped: v.scrapped,
        avgCycle: v.cycles.length ? v.cycles.reduce((a, b) => a + b, 0) / v.cycles.length : null,
        days: v.days.size,
        target: lookups.stations.get(id)?.target_seconds ?? null,
      }))
      .sort((a, b) => b.units - a.units);
  }, [daily, lookups]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{totals.good.toLocaleString()}</span> units in the last {days} days
          {totals.scrap ? <span className="text-fresh-salmon-dark"> · {totals.scrap} scrapped ({totals.rate == null ? '—' : `${(totals.rate * 100).toFixed(1)}%`})</span> : null}
          {totals.perActiveDay ? ` · ${totals.perActiveDay.toFixed(1)} per working day` : ''}
        </p>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                days === r ? 'border-info bg-info-soft/30 text-info' : 'border-border text-muted-foreground hover:bg-secondary',
              )}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <section className="rounded-2xl border border-border bg-card p-4">
        <h3 className="text-sm font-medium text-foreground">Units per day</h3>
        {dailyLoading ? (
          <Skeleton className="mt-4 h-40" />
        ) : (
          <div className="mt-4 flex h-44 items-end gap-1">
            {perDay.map((d) => (
              <div
                key={d.key}
                className="flex flex-1 flex-col items-center justify-end gap-px"
                title={`${formatDateOnly(d.key)}: ${d.good} good, ${d.scrap} scrap${d.avgCycle ? `, avg ${formatDuration(d.avgCycle)}` : ''}`}
              >
                {d.scrap > 0 && <div className="w-full rounded-t-sm bg-fresh-salmon" style={{ height: `${(d.scrap / max) * 100}%` }} />}
                <div className={cn('w-full bg-bright-moss-dark', d.scrap === 0 && 'rounded-t-sm')} style={{ height: d.good ? `${(d.good / max) * 100}%` : 2 }} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
          <span>{perDay[0] ? formatDateOnly(perDay[0].key) : ''}</span>
          <span>{perDay[perDay.length - 1] ? formatDateOnly(perDay[perDay.length - 1].key) : ''}</span>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-medium text-foreground">By station</h3>
        </header>
        {dailyLoading ? (
          <Skeleton className="m-4 h-24" />
        ) : byStation.length === 0 ? (
          <div className="p-4">
            <QuietEmpty>No units logged in this range.</QuietEmpty>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Station</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Scrap</TableHead>
                  <TableHead className="text-right">Avg cycle</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Efficiency</TableHead>
                  <TableHead className="text-right">Active days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byStation.map((s) => {
                  const rate = scrapRate(s.units, s.scrapped);
                  const eff = s.avgCycle && s.target ? s.target / s.avgCycle : null;
                  return (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium text-foreground">{s.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{s.units}</TableCell>
                      <TableCell className={cn('text-right tabular-nums', rate !== null && rate > 0.05 && 'text-fresh-salmon-dark')}>
                        {s.scrapped}
                        {rate !== null && rate > 0 ? ` (${(rate * 100).toFixed(1)}%)` : ''}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatDuration(s.avgCycle)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatDuration(s.target)}</TableCell>
                      <TableCell className={cn('text-right tabular-nums', eff !== null && (eff >= 1 ? 'text-bright-moss-dark' : eff < 0.85 ? 'text-fresh-salmon-dark' : ''))}>
                        {eff === null ? '—' : `${Math.round(eff * 100)}%`}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{s.days}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-medium text-foreground">Shifts</h3>
        </header>
        {sessionsLoading ? (
          <Skeleton className="m-4 h-40" />
        ) : sessions.length === 0 ? (
          <div className="p-4">
            <QuietEmpty>No shifts in this range.</QuietEmpty>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Station</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead className="text-right">Length</TableHead>
                    <TableHead className="text-right">Units</TableHead>
                    <TableHead className="text-right">Scrap</TableHead>
                    <TableHead>Ended</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.slice(0, shown).map((s) => (
                    <SessionRow key={s.id} session={s} lookups={lookups} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {sessions.length > shown && (
              <div className="flex justify-center border-t border-border p-3">
                <Button variant="ghost" size="sm" onClick={() => setShown((n) => n + 40)}>
                  Show more ({sessions.length - shown} left)
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function SessionRow({ session: s, lookups }: { session: WorkSession; lookups: WorkLookups }) {
  const end = s.ended_at ? Date.parse(s.ended_at) : Date.now();
  const length = (end - Date.parse(s.started_at)) / 1000;
  return (
    <TableRow>
      <TableCell>
        <WorkerChip person={lookups.person(s.user_id)} size={24} />
      </TableCell>
      <TableCell className="text-sm">{lookups.stationName(s.workstation_id)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{lookups.itemName(s.item_id) ?? '—'}</TableCell>
      <TableCell className="whitespace-nowrap text-sm">{formatDateTime(s.started_at)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatDuration(length)}</TableCell>
      <TableCell className="text-right tabular-nums">{s.units_completed}</TableCell>
      <TableCell className={cn('text-right tabular-nums', s.units_scrapped > 0 && 'text-fresh-salmon-dark')}>{s.units_scrapped}</TableCell>
      <TableCell>
        {s.ended_at ? (
          <span className="text-xs text-muted-foreground">{s.end_reason ? END_REASON_LABEL[s.end_reason] : 'Ended'}</span>
        ) : (
          <Badge variant="success-soft">Open</Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
